import { cn } from "@hybrid-iptv/ui";
import { useFavoritesStore, type FavoriteKind } from "./favoritesStore.js";

interface FavoriteStarProps {
  kind: FavoriteKind;
  ref: string;
  /** Used in the aria-label; falls back to the ref. */
  label?: string;
  className?: string;
}

/**
 * Star toggle for any favoritable record (Section 11). Optimistic like the
 * store's toggle: the star flips instantly and the write-through to IndexedDB
 * happens in the background. Overlay-positioned by default (`.fav-star` is
 * absolute) so it can sit on top of a card; pass `className="fav-star--left"`
 * to avoid the resume badge, or override position where it should be inline.
 */
export function FavoriteStar({ kind, ref, label, className }: FavoriteStarProps) {
  const isFavorite = useFavoritesStore((s) => s.isFavorite(kind, ref));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  return (
    <button
      type="button"
      className={cn("fav-star", isFavorite && "fav-star--on", className)}
      aria-pressed={isFavorite}
      aria-label={isFavorite ? `Remove ${label ?? ref} from favorites` : `Add ${label ?? ref} to favorites`}
      onClick={(e) => {
        // The star may sit on top of a card button — never bubble the toggle.
        e.stopPropagation();
        toggleFavorite(kind, ref);
      }}
    >
      {isFavorite ? "★" : "☆"}
    </button>
  );
}
